// src/features/links/ManualAddDialog.tsx
// IR-1047 Phase 3 (ADD-01) — Manual Add modal. Minimal field set: the raw group
// link + "Admin approval expected". Platform/Type are auto-detected server-side
// from the link (Notes/Type fields deferred — no column yet). On success the
// list invalidates and the dialog closes.
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { ApiError } from "@/lib/api";
import { useState } from "react";
import { useCreateLink } from "./api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManualAddDialog({ open, onOpenChange }: Props) {
  const [link, setLink] = useState("");
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateLink();

  function reset() {
    setLink("");
    setApprovalRequired(false);
    setError(null);
  }

  function close() {
    reset();
    onOpenChange(false);
  }

  function submit() {
    setError(null);
    create.mutate(
      { link: link.trim(), approval_required: approvalRequired },
      {
        onSuccess: () => close(),
        onError: (e) => {
          const detail =
            e instanceof ApiError && e.body && typeof e.body === "object"
              ? (e.body as { detail?: string }).detail
              : undefined;
          setError(
            detail === "unsupported_link_type"
              ? "Not a supported IM link (Telegram, WhatsApp, or Signal)."
              : detail === "link_required"
                ? "Enter a group link."
                : "Could not add the link. Check the URL and try again.",
          );
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, padding: 20 }}>
        <DialogTitle>Add link manually</DialogTitle>
        <DialogDescription>
          Paste a Telegram, WhatsApp, or Signal group link. Platform is detected automatically.
        </DialogDescription>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--iris-fg-3)]">Group link</span>
          <input
            className="iris-input"
            type="text"
            value={link}
            placeholder="https://t.me/+…"
            aria-label="Group link"
            onChange={(e) => setLink(e.target.value)}
          />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={approvalRequired}
            aria-label="Admin approval expected"
            onChange={(e) => setApprovalRequired(e.target.checked)}
          />
          <span>Admin approval expected</span>
        </label>

        {error && <p className="text-xs text-[var(--iris-neon-pink)]">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="iris-btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="iris-btn iris-btn--primary"
            disabled={!link.trim() || create.isPending}
            onClick={submit}
          >
            {create.isPending ? "Adding…" : "Add link"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
